/**
 * Shared data-source tooltip formatter for the Screener and Watchlist result
 * tables (and the Live/Delayed badge in OptionsPremiumChartModal).
 *
 * Every engine row carries a `_meta` dict with one entry per field family
 * (`current_premium`, `implied_volatility`, `greeks`, `underlying_price`,
 * `volume`, ...). Each entry has `source` ('P' Polygon/Massive, 'E' EODHD),
 * `timestamp` (ms) and sometimes `is_default`.
 *
 * On the Massive individual plan the backend adds provenance fields:
 *   - current_premium:    price_source ('fmv'|'mid'|'last_trade'|'day_close'|'mid_wide'|'none'), timeframe
 *   - implied_volatility: iv_source ('provider'|'computed'|'unavailable'), price_source, timeframe,
 *                         and since the app-IV switch: used ('app'|'massive'|'default'),
 *                         app_iv, massive_iv (decimals), app_iv_reason -> formatIvTooltip
 *   - greeks:             greeks_source ('provider'|'computed'|'unavailable'), timeframe
 *   - bid_ask:            timeframe
 * `timeframe` is the provider's 'DELAYED' / 'REAL-TIME' label.
 *
 * Output format (parts joined by single spaces, empty parts dropped):
 *   <P.|E.> [qualifier] [M/D/YYYY h:mm AM TZ] [(delayed)]
 * e.g. "P. calc. from mid 9/28/2026 10:00 AM EDT (delayed)".
 *
 * An entry WITHOUT the new fields formats byte-identically to the old
 * per-table getDataSourceTooltip text ("P. 12/12/2025 10:54 AM EST", "E.",
 * "def. (default value used)"), so older cached results and EODHD rows read
 * exactly as before.
 */

// Short labels for the price ladder rung that priced the contract.
// 'none' is deliberately absent: an unpriced contract gets no qualifier.
export const PRICE_SOURCE_LABELS = {
  fmv: 'FMV',
  mid: 'mid',
  last_trade: 'last trade',
  day_close: 'day close',
  mid_wide: 'wide mid',
};

const SOURCE_PREFIXES = {
  P: 'P.', // Polygon.io / Massive
  E: 'E.', // EODHD
};

// Own-key lookup so a stray value like 'constructor' can't hit Object.prototype.
const lookup = (map, key) =>
  (typeof key === 'string' && Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined);

// Same toLocale* options the tables have always used, so the timestamp part
// is unchanged (it renders in the viewer's local timezone).
const formatTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  const datePart = date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
  return `${datePart} ${timePart}`;
};

// Provenance qualifier for one _meta entry, or '' when there is nothing to add.
const getQualifier = (info, metaKey) => {
  if (metaKey === 'implied_volatility' && info.iv_source === 'computed') {
    // IV inverted locally from a quote (Massive sent none).
    return `calc. from ${lookup(PRICE_SOURCE_LABELS, info.price_source) || 'quote'}`;
  }
  if (metaKey === 'greeks') {
    if (info.greeks_source === 'computed') return 'calc.';
    // The cells show NA here; say why instead of implying a provider value.
    if (info.greeks_source === 'unavailable') return 'unavailable';
    return '';
  }
  if (metaKey === 'current_premium') {
    return lookup(PRICE_SOURCE_LABELS, info.price_source) || '';
  }
  return '';
};

/**
 * Format the hover tooltip for one `_meta` entry.
 *
 * @param {object} info     row._meta[metaKey]
 * @param {string} metaKey  which _meta entry it is ('current_premium',
 *                          'implied_volatility', 'greeks', ...). Only used
 *                          to pick the provenance qualifier.
 * @returns {string|null}   null when there is no usable metadata (missing
 *                          entry, or an unknown source such as results cached
 *                          before _meta existed) so the cell shows no tooltip.
 */
export function formatDataSourceTooltip(info, metaKey) {
  if (!info || typeof info !== 'object') return null;

  // Rows built since the app-IV switch carry both IVs: show them side by side.
  if (metaKey === 'implied_volatility' && info.used) {
    return formatIvTooltip(info);
  }

  // A defaulted value (e.g. IV fell back to 0.30) says so and nothing else.
  if (info.is_default) {
    return 'def. (default value used)';
  }

  const prefix = lookup(SOURCE_PREFIXES, info.source);
  if (!prefix) return null;

  const parts = [prefix, getQualifier(info, metaKey)];
  if (info.timestamp) parts.push(formatTimestamp(info.timestamp));
  if (info.timeframe === 'DELAYED') parts.push('(delayed)');

  return parts.filter(Boolean).join(' ');
}

// Why the app could not solve an IV (snapshot_enrichment / options_math reason codes).
export const IV_REASON_LABELS = {
  price_source_ineligible: 'no usable price (wide or stale quote)',
  quote_underlying_skew: 'quote and stock price too far apart in time',
  stale_trade: 'last trade older than 15 min',
  below_intrinsic: 'price below intrinsic value',
  below_european_bound: 'price below the European lower bound',
  above_upper_bound: 'price above the model upper bound',
  no_time_value: 'no time value left',
  american_itm_put: 'in-the-money American put',
  no_price: 'no price',
  no_underlying: 'no stock price',
  too_close_to_expiry: 'under 5 minutes to expiry',
  expired: 'expired',
  iv_out_of_range: 'IV above 500%',
  iv_below_floor: 'IV below 5%',
  no_convergence: 'solver did not converge',
  bad_contract_details: 'incomplete contract details',
  nonstandard_deliverable: 'non-standard contract size',
  enrich_error: 'malformed contract data',
};

const pctText = (v) => (typeof v === 'number' && Number.isFinite(v) ? `${(v * 100).toFixed(1)}%` : null);
const reasonText = (code) => lookup(IV_REASON_LABELS, code) || code || 'not solvable';

/**
 * Multi-line IV tooltip: the IV the calculations used, the app's IV, Massive's
 * IV, and the usual source/time line. `ivMeta` is row._meta.implied_volatility
 * with the comparison fields (used, app_iv, massive_iv, app_iv_reason).
 *
 * @param {object} ivMeta
 * @param {string} [heading] optional first line, e.g. 'Latest simulation'
 */
export function formatIvTooltip(ivMeta, heading) {
  if (!ivMeta || typeof ivMeta !== 'object') return null;
  const app = pctText(ivMeta.app_iv);
  const massive = pctText(ivMeta.massive_iv);
  const why = reasonText(ivMeta.app_iv_reason);
  const lines = [];
  if (heading) lines.push(heading);

  if (ivMeta.used === 'app') {
    const from = lookup(PRICE_SOURCE_LABELS, ivMeta.price_source) || 'quote';
    const anchored = ivMeta.underlying_source === 'anchored' ? ', stock price at quote time' : '';
    lines.push(`IV used: App ${app || '–'} (solved from the ${from}${anchored})`);
  } else if (ivMeta.used === 'massive') {
    lines.push(`IV used: Massive ${massive || '–'} (app couldn't solve: ${why})`);
  } else {
    lines.push('IV used: 30% placeholder (no app or Massive IV)');
  }

  lines.push(`App IV: ${app || `– (${why})`}`);
  if (massive) {
    const ignored = ivMeta.used === 'default' ? ' (outside 5%–500%, not used)' : '';
    lines.push(`Massive IV: ${massive}${ignored}`);
  } else {
    lines.push('Massive IV: – (not sent)');
  }

  const prefix = lookup(SOURCE_PREFIXES, ivMeta.source);
  if (prefix) {
    const parts = [prefix];
    if (ivMeta.timestamp) parts.push(formatTimestamp(ivMeta.timestamp));
    if (ivMeta.timeframe === 'DELAYED') parts.push('(delayed)');
    lines.push(parts.join(' '));
  }
  return lines.join('\n');
}

/**
 * IV tooltip for an engine row (screener / watchlist simulation row), or null
 * when the row carries no IV metadata. Older cached rows without the
 * comparison fields get the legacy one-line text.
 */
export function getIvTooltip(row, heading) {
  const ivMeta = row?._meta?.implied_volatility;
  if (!ivMeta) return null;
  if (ivMeta.used) return formatIvTooltip(ivMeta, heading);
  const legacy = formatDataSourceTooltip(ivMeta, 'implied_volatility');
  return legacy && heading ? `${heading}\n${legacy}` : legacy;
}

/**
 * Whether the premium quote should be badged "Live" (true) or "Delayed" (false).
 *
 * Prefers the provider's timeframe label (_meta.current_premium.timeframe).
 * Without one (older payloads, EODHD rows) it keeps the legacy rule: an
 * unknown source or Polygon ('P') is Live and EODHD ('E') is Delayed.
 */
export function isLiveQuote(info) {
  const timeframe = info?.timeframe;
  if (timeframe === 'DELAYED') return false;
  if (timeframe === 'REAL-TIME') return true;
  const source = info?.source;
  return !source || source === 'P';
}
